import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { loginRequest } from '../authConfig';

function LogsPage() {
  const { instance, accounts } = useMsal();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLogs();
  }, []);

  const getAccessToken = async () => {
    const account = accounts[0];
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account
    });

    return tokenResponse.accessToken;
  };

  const loadLogs = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      const response = await fetch('/api/logs', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load logs');
      }

      setLogs(data);
    } catch (err) {
      console.error('Error loading logs', err);
      setError(err.message || 'Unable to load logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3.5, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
        <Stack spacing={1.25}>
          <Chip label="Audit & Accountability" color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
          <Typography variant="h2">Operational Audit Trail</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 820 }}>
            Audit entries should capture who generated a pool, who changed a setting, when a police
            batch was exported or imported, and any later invalidation before the final jury is seated.
          </Typography>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress />
            <Typography color="text.secondary">Loading audit records…</Typography>
          </Stack>
        ) : logs.length === 0 ? (
          <Alert severity="info">No audit entries are available yet.</Alert>
        ) : (
          <TableContainer>
            <Table aria-label="audit log table">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.LogId}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.TimestampLabel || log.Timestamp}</TableCell>
                    <TableCell>{log.UserId}</TableCell>
                    <TableCell>
                      <Chip size="small" label={log.Action} variant="outlined" color="primary" />
                    </TableCell>
                    <TableCell>{[log.EntityType, log.EntityId].filter(Boolean).join(' / ') || 'System'}</TableCell>
                    <TableCell sx={{ maxWidth: 420, wordBreak: 'break-word' }}>
                      {log.DetailsSummary || 'No additional details'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}

export default LogsPage;
